import React, { useState } from "react";
import { Info } from "lucide-react";
import { DocStatus, isInKnowledge, isSupportBotApprover, useSupportBotDocs } from "./store";
import { Chip, EmptyState, PageHeader, primaryBtn, StatusBadge } from "./shared";
import UploadModal from "./upload-modal";

type Filter = "all" | DocStatus;

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "live", label: "Live" },
  { key: "pending", label: "Pending approval" },
  { key: "changes_requested", label: "Changes requested" },
  { key: "failed", label: "Failed" },
];

// Support Bot > Knowledge. CRM Admins see every document with stat cards and
// status filters; agents only see what the bot is live with today, plus a
// note that their own uploads go through review first.
const KnowledgePage: React.FC = () => {
  const approver = isSupportBotApprover();
  const docs = useSupportBotDocs().filter(isInKnowledge);
  const [filter, setFilter] = useState<Filter>("all");
  const [uploadOpen, setUploadOpen] = useState(false);

  const count = (s: DocStatus) => docs.filter((d) => d.status === s).length;
  const visible = approver ? docs.filter((d) => filter === "all" || d.status === filter) : docs.filter((d) => d.status === "live");

  const stats = [
    { label: "Documents", value: docs.length, cls: "text-gray-900 dark:text-gray-100" },
    { label: "Live in bot", value: count("live"), cls: "text-emerald-600 dark:text-emerald-400" },
    { label: "Pending approval", value: count("pending"), cls: "text-amber-600 dark:text-amber-400" },
    { label: "Failed validation", value: count("failed"), cls: "text-rose-600 dark:text-rose-400" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-6">
        <PageHeader
          title="Knowledge"
          subtitle={approver ? "Documents your Support Bot learns from." : "Documents the Support Bot is live with today."}
          action={
            <button type="button" onClick={() => setUploadOpen(true)} className={primaryBtn}>
              Upload documents
            </button>
          }
        />

        {approver ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {stats.map((s) => (
                <div key={s.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">{s.label}</p>
                  <p className={`text-2xl font-semibold mt-2 ${s.cls}`}>{s.value}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <Chip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
                  {f.label} ({f.key === "all" ? docs.length : count(f.key)})
                </Chip>
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/60 dark:bg-indigo-500/10 px-4 py-3 text-sm text-indigo-700 dark:text-indigo-300">
            <Info size={15} className="shrink-0" />
            Uploads from agents are reviewed by a CRM Admin before the bot uses them.
          </div>
        )}

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-x-auto">
          {visible.length === 0 ? (
            <EmptyState text={approver && filter !== "all" ? "No documents with this status." : "No documents yet — upload one to get started."} />
          ) : (
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide uppercase border-b border-gray-100 dark:border-gray-800">
                  <th className="px-5 py-3">Document</th>
                  <th className="px-3 py-3">Product</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">Visible to</th>
                  {/* <th className="px-3 py-3">Uploaded by</th> */}
                  <th className="px-3 py-3">Status</th>
                  <th className="px-5 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => (
                  <tr key={d.id} className="border-b last:border-0 border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-3.5 font-semibold text-gray-900 dark:text-gray-100">{d.name}</td>
                    <td className="px-3 py-3.5 text-gray-600 dark:text-gray-300">{d.product}</td>
                    <td className="px-3 py-3.5 text-gray-600 dark:text-gray-300">{d.type}</td>
                    <td className="px-3 py-3.5 text-gray-600 dark:text-gray-300">{d.visibleTo.join(", ")}</td>
                    {/* <td className="px-3 py-3.5 text-gray-600 dark:text-gray-300">{d.uploadedBy}</td> */}
                    <td className="px-3 py-3.5">
                      <StatusBadge status={d.status} />
                    </td>
                    <td className="px-5 py-3.5 text-gray-400 dark:text-gray-500 whitespace-nowrap">{d.updated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} />}
    </div>
  );
};

export default KnowledgePage;
